import { signOut } from "firebase/auth";
import { auth, db } from "../config/firebase";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, doc, getDoc, addDoc } from "firebase/firestore";
import NavBar from "../components/navbar";
import Footer from "../components/footer";
import "./Dashboard.css";

export default function Dashboard({ user }) {
    const [userName, setUserName] = useState("");
    const [students, setStudents] = useState([]);
    const [registrations, setRegistrations] = useState([]); // New state for registrations
    const [courses, setCourses] = useState([]); // New state for courses
    const [showModal, setShowModal] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [studentBirthdate, setStudentBirthdate] = useState('');
    const navigate = useNavigate();
    const calculateAge = (birthdate) => {
        const birthDateObj = new Date(birthdate);
        const currentDate = new Date();
        let age = currentDate.getFullYear() - birthDateObj.getFullYear();
        const monthDiff = currentDate.getMonth() - birthDateObj.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDateObj.getDate())) {
            age--;
        }
        return age;
    };
    
    // Fetch User Details
    useEffect(() => {
        if (user) {
            const fetchUserDetails = async () => {
                try {
                    const userDocRef = doc(db, `Users/${user.uid}`);
                    const userDoc = await getDoc(userDocRef);
                    if (userDoc.exists()) {
                        setUserName(userDoc.data().firstName);
                    } else {
                        console.log("No such document!");
                    }
                } catch (error) {
                    console.error("Error fetching user data:", error);
                }
            };
            fetchUserDetails();
        }
    }, [user]);

    // Fetch Students
    useEffect(() => {
        if (user) {
            const fetchStudents = async () => {
                try {
                    const studentsRef = collection(db, `Users/${user.uid}/Students`);
                    const studentsSnapshot = await getDocs(studentsRef);
                    const studentsData = studentsSnapshot.docs.map((doc) => ({
                        id: doc.id,
                        ...doc.data(),
                    }));
                    setStudents(studentsData);
                } catch (error) {
                    console.error("Error fetching students:", error);
                }
            };
            fetchStudents();
        }
    }, [user]);

    // Fetch Courses
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const coursesRef = collection(db, `Course`);
                const coursesSnapshot = await getDocs(coursesRef);
                const coursesData = coursesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setCourses(coursesData);
            } catch (error) {
                console.error("Error fetching courses:", error);
            }
        };
        fetchCourses();
    }, []);

// Fetch Registrations
useEffect(() => {
    if (user) {
        const fetchRegistrations = async () => {
            try {
                const registrationsRef = collection(db, `Users/${user.uid}/Enrollments`);
                const registrationsSnapshot = await getDocs(registrationsRef);
                const registrationsData = registrationsSnapshot.docs
                    .map((doc) => ({
                        id: doc.id,
                        ...doc.data(),
                    }))
                    .filter(
                        (registration) =>
                            registration.studentId && registration.courseId // Ensure both IDs exist
                    );

                // Remove duplicates based on studentId and courseId
                const uniqueRegistrations = Array.from(
                    new Set(registrationsData.map((a) => a.studentId + a.courseId))
                ).map((id) =>
                    registrationsData.find((a) => a.studentId + a.courseId === id)
                );

                setRegistrations(uniqueRegistrations);
            } catch (error) {
                console.error("Error fetching registrations:", error);
            }
        };
        fetchRegistrations();
    }
}, [user]);


    // Handle Sign Out
    const handleSignOut = () => {
        signOut(auth)
            .then(() => console.log("Sign Out"))
            .catch((error) => console.log(error));
    };

    // Add Student
    const addStudent = async (e) => {
        e.preventDefault();

        // Check if all required fields are filled
        if (!firstName || !lastName || !studentBirthdate) {
            alert("Please fill in all fields.");
            return;
        }

        // Create a normalized version of the student's full name and birthdate
        const normalizedFullName = `${firstName} ${middleName} ${lastName}`.toLowerCase().trim();
        const normalizedBirthdate = studentBirthdate.trim();

        // Check if the student already exists
        const studentExists = students.some(student => {
            const existingFullName = `${student.firstName} ${student.middleName} ${student.lastName}`.toLowerCase().trim();
            return existingFullName === normalizedFullName && student.birthdate === normalizedBirthdate;
        });

        if (studentExists) {
            alert("This student already exists!");
            return;
        }

        try {
            const newStudent = {
                firstName: firstName,
                middleName: middleName,
                lastName: lastName,
                birthdate: studentBirthdate,
                email: user.email,
                phoneNumber: user.phoneNumber,
                coursesCompleted: [], // Ensure this is initialized as an empty array if no courses completed
            };

            // Add the new student to the database
            const docRef = await addDoc(collection(db, `Users/${user.uid}/Students`), newStudent);

            // Update the state to reflect the added student
            setStudents((prevStudents) => [
                ...prevStudents,
                { id: docRef.id, ...newStudent },
            ]);

            // Clear form fields and close the modal
            setFirstName('');
            setMiddleName('');
            setLastName('');
            setStudentBirthdate('');
            setShowModal(false);
            alert("Student added successfully!");
        } catch (error) {
            console.error("Error adding student:", error);
            alert("Failed to add student.");
        }
    };

    // Toggle Modal
    const toggleModal = () => setShowModal(!showModal);

    return (
        <div className="dashboard">
            <NavBar />
            <div className="dashboard-main">
                <div className="dashboard-greeting">
                    <h2>Welcome, {userName}!</h2>
                    <p>Here's an overview of your students and activities.</p>
                </div>

                <div className="students-section">
                    <h3>Your Students</h3>
                    <table className="students-table">
                        <thead>
                            <tr>
                                <th>First Name</th>
                                <th>Middle Name</th>
                                <th>Last Name</th>
                                <th>Age</th>
                                <th>Birthdate</th>
                                <th>Email</th>
                                <th>Phone Number</th>
                            </tr>
                        </thead>
                        <tbody>
                            {students.length > 0 ? (
                                students.map((student) => (
                                    <tr key={student.id}>
                                        <td>{student.firstName}</td>
                                        <td>{student.middleName}</td>
                                        <td>{student.lastName}</td>
                                        <td>{calculateAge(student.birthdate)}</td>
                                        <td>{student.birthdate}</td>
                                        <td>{student.email}</td>
                                        <td>{student.phoneNumber}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7">No students found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="registrations-section">
    <h3>Your Registrations</h3>
    <table className="students-table">
        <thead>
            <tr>
                <th>Student Name</th>
                <th>Age</th>
                <th>Course</th>
            </tr>
        </thead>
        <tbody>
    {registrations.length > 0 ? (
        registrations.map((registration) => {
            if (!registration || !registration.studentId || !registration.courseId) {
                return null; // Skip invalid registrations
            }

            const student = students.find((s) => s.id === registration.studentId);
            const course = courses.find((c) => c.id === registration.courseId);

            return (
                <tr key={registration.id}>
                    <td>
                        {student
                            ? `${student.firstName} ${student.middleName || ""} ${student.lastName}`.trim()
                            : "Unknown Student"}
                    </td>
                    <td>
                        {student && student.birthdate
                            ? calculateAge(student.birthdate)
                            : "N/A"}
                    </td>
                    <td>{course ? course.Title : "Unknown Course"}</td>
                </tr>
            );
        })
    ) : (
        <tr>
            <td colSpan="3">No registrations found</td>
        </tr>
    )}
</tbody>

    </table>
</div>


                {/* Dashboard Actions */}
                <div className="dashboard-actions">
                    <button className="primary-button" onClick={toggleModal}>
                        Add New Student
                    </button>
                    <button className="secondary-button" onClick={handleSignOut}>
                        Sign Out
                    </button>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="close-button" onClick={toggleModal}>X</button>
                        <h3>Add a New Student</h3>
                        <form onSubmit={addStudent} className="add-student-form">
                            <label htmlFor="firstName">First Name</label>
                            <input
                                type="text"
                                id="firstName"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                            />
                            <label htmlFor="middleName">Middle Name</label>
                            <input
                                type="text"
                                id="middleName"
                                value={middleName}
                                onChange={(e) => setMiddleName(e.target.value)}                            />
                                <label htmlFor="lastName">Last Name</label>
                                <input
                                    type="text"
                                    id="lastName"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                />
                                <label htmlFor="studentBirthdate">Birthdate</label>
                                <input
                                    type="date"
                                    id="studentBirthdate"
                                    value={studentBirthdate}
                                    onChange={(e) => setStudentBirthdate(e.target.value)}
                                    required
                                />
                                <button type="submit" className="primary-button">
                                    Add Student
                                </button>
                            </form>
                        </div>
                    </div>
                )}
                <Footer />
            </div>
        );
    }
    
